import React, {useEffect, useRef, useState} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle } from 'lucide-react';
import { authService } from '../../services/authService';
import icon from '../../img/icon.png';
import { useLanguage } from '../../hooks/useLanguage';
import i18n from "i18next";

interface LoginFormProps {
  onClose?: () => void;
  isModal?: boolean;
}

interface FormData {
  email: string;
  password: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onClose, isModal = false }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);



  const setCustomValidationMessages = () => {
    if (emailInputRef.current) {
      emailInputRef.current.setCustomValidity('');
      emailInputRef.current.addEventListener('invalid', () => {
        if (emailInputRef.current?.validity.valueMissing) {
          emailInputRef.current.setCustomValidity(t('loginPage.validation.emailRequired'));
        } else if (emailInputRef.current?.validity.typeMismatch) {
          emailInputRef.current.setCustomValidity(t('loginPage.validation.emailInvalid'));
        }
      });

      emailInputRef.current.addEventListener('input', () => {
        emailInputRef.current?.setCustomValidity('');
      });
    }

    if (passwordInputRef.current) {
      passwordInputRef.current.setCustomValidity('');
      passwordInputRef.current.addEventListener('invalid', () => {
        if (passwordInputRef.current?.validity.valueMissing) {
          passwordInputRef.current.setCustomValidity(t('loginPage.validation.passwordRequired'));
        }
      });

      passwordInputRef.current.addEventListener('input', () => {
        passwordInputRef.current?.setCustomValidity('');
      });
    }
  };
  useEffect(() => {
    setCustomValidationMessages();
  }, [i18n.language, t]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setEmailSent(false);

      const response = await authService.login(formData);

      // Email non vérifié = email automatiquement envoyé
      if (response.emailNotVerified && response.emailSent) {
        setError(t('loginPage.emailNotVerified'));
        setEmailSent(true);
        return;
      }

      // Connexion réussie
      if (onClose) {
        onClose();
      }
      navigate('/dashboard', { replace: true });

    } catch (error: any) {
      setError(error.message || t('loginPage.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setEmailSent(false);
    setError('');
  };

  const containerClass = isModal
      ? "p-6"
      : "min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8";

  return (
      <div className={`${containerClass} ${isRTL ? 'rtl' : ''}`}>
        <div className={`max-w-md w-full ${!isModal && 'space-y-8 bg-white rounded-lg shadow-xl p-8'}`}>
          <div className="flex flex-col items-center">
            <img src={icon} alt="Inspecly Logo" width={80} height={80} />
            <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
              {t('loginPage.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('loginPage.subtitle')}
            </p>
          </div>

          {emailSent ? (
              // Écran quand l'email est envoyé
              <div className="mt-8">
                <div className="rounded-md bg-blue-50 p-4 text-sm border border-blue-200 mb-4">
                  <div className="flex items-start">
                    <CheckCircle className={`h-5 w-5 text-blue-400 ${isRTL ? 'ml-2' : 'mr-2'} mt-0.5`} />
                    <div>
                      <h3 className="text-blue-800 font-medium mb-2">
                        {t('loginPage.verification.title')}
                      </h3>
                      <p className="text-blue-700">
                        {t('loginPage.verification.description', { email: formData.email })}
                      </p>
                      <p className="text-blue-600 text-xs mt-2">
                        {t('loginPage.verification.validity')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-3">
                  <button
                      type="button"
                      onClick={handleBackToLogin}
                      className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t('loginPage.verification.backToLogin')}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    {t('loginPage.verification.afterVerification')}
                  </p>
                </div>
              </div>
          ) : (
              // Formulaire de connexion normal
              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                {error && (
                    <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 border border-red-200">
                      <div className="flex items-center">
                        <svg className={`h-5 w-5 text-red-400 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </div>
                )}

                <div className="rounded-md shadow-sm -space-y-px">
                  <div>
                    <label htmlFor="email" className="sr-only">
                      {t('loginPage.form.email')}
                    </label>
                    <input
                        ref={emailInputRef}
                        id="email"
                        name="email"
                        type="email"
                        required
                        className={`appearance-none rounded-t-md relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                        placeholder={t('loginPage.form.emailPlaceholder')}
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          // Réinitialiser la validation personnalisée lors de la saisie
                          e.target.setCustomValidity('');
                        }}                        dir="ltr"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="sr-only">
                      {t('loginPage.form.password')}
                    </label>
                    <input
                        ref={passwordInputRef}
                        id="password"
                        name="password"
                        type="password"
                        required
                        className={`appearance-none rounded-b-md relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                        placeholder={t('loginPage.form.passwordPlaceholder')}
                        value={formData.password}
                        onChange={(e) => {
                          setFormData({ ...formData, password: e.target.value });
                          e.target.setCustomValidity('');
                        }}
                        dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <button
                      type="button"
                      onClick={() => navigate('/forget-password')}
                      className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    {t('loginPage.form.forgotPassword')}
                  </button>
                  <Link
                      to="/register"
                      state={{ subscription: "STANDARD", fromPricing: true }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    {t('loginPage.form.createAccount')}
                  </Link>
                </div>

                <div>
                  <button
                      type="submit"
                      disabled={loading}
                      className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150 ${
                          loading ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {loading ? (
                        <span className="flex items-center">
                          <svg className={`animate-spin ${isRTL ? 'mr-1 ml-3' : '-ml-1 mr-3'} h-5 w-5 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('loginPage.form.loggingIn')}
                        </span>
                    ) : (
                        t('loginPage.form.login')
                    )}
                  </button>
                </div>

                {/* Indication utile pour l'utilisateur */}
                <div className="text-center">
                  <p className="text-xs text-gray-500">
                    {t('loginPage.form.verificationNote')}
                  </p>
                </div>
              </form>
          )}
        </div>
      </div>
  );
};